import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useParams } from 'react-router-dom';
import { MainContentBox, DataGridListScrollBox, PageTitleToolbarGrid, MainDataGrid } from '../../util/SharedStyles';
import { GridRenderCellParams, GridColumns, GridRowModel, GridOverlay, GridSortModel, GridToolbarContainer, useGridApiContext, GridSortItem, GridSelectionModel, GridRowParams, GridCsvExportOptions, GridApi } from '@mui/x-data-grid-pro';
import { CircularProgress, Button, Dialog, Link, styled } from '@mui/material';
import { debounce } from 'lodash';

import { useTitle, capitalizeWithUnderscoreRemoval, PremadeDocumentArchiveFilterSet, downloadDocument, getUniqueFileName } from '../../util/Common';
import { Viewer, defaultGridListPageSize, ToastSeverity, documentArchiveUnacknowledgedFilterDate, pageSizeOptions } from '../../util/Constants';
import { getShortDateString, getTimeString } from '../../util/DateTimeUtility';
import { viewerCanEdit } from '../../util/ViewerUtility';

import PageTitleBar from '../../components/PageTitleBar';
import ErrorMessage from '../../components/ErrorMessage';
import NoResultsMessage from '../../components/NoResultsMessage';
import { 
    clearState, 
    clearError,
    selectError, 
    selectRequestsInFlight, 
    selectDocumentArchiveList, 
    selectDocumentArchivePagingResult,
    selectAcknowledgeDocumentArchivesStatus,
    selectResendEmailsStatus,
    selectAbandonDocumentArchivesStatus,
    captureAbandonDocumentArchivesStatus,
    captureAcknowledgeDocumentArchivesStatus,
    captureResendEmailsStatus,
    selectGetDocumentHtmlFileStatus,
    captureGetDocumentHtmlFileStatus,
    selectGetDocumentAcknowledgementFileStatus,
    captureGetDocumentAcknowledgementFileStatus,
    DownloadOperation,
    selectDocumentArchiveById,
    fetchDocumentArchiveByIdSuccess,
    FilterOptions,
    selectCurrentPage
} from './DocumentArchivesSlice';
import { fetchDocumentArchiveList, abandonDocumentArchives, acknowledgeDocumentArchives, resendEmails, fetchDocumentArchiveById, getDocumentHtmlFile, getDocumentAcknowledgementFile, upsertDocumentArchiveNote, deleteDocumentArchiveNote } from './DocumentArchivesActions';
import MainDataGridLoadingSkeleton from '../../components/MainDataGridLoadingSkeleton';
import DocumentArchivesFilterBar from '../../components/filters/DocumentArchivesFilterBar';
import FiltersButton from '../../components/buttons/FiltersButton';
import GridCellDualVert from '../../components/listItems/GridCellDualVert';
import { GetDocumentArchivesInput, DocumentArchiveOrderByInput, OrderDirection, DocumentAcknowledgementStatus, RequestResult } from '../../gql-types.generated';
import { setToastConfig } from '../EDIContainer/EDIContainerSlice';
import ConfirmationPrompt from '../../components/ConfirmationPrompt';
import DocumentArchiveDetailsDialog from '../../components/dialogs/DocumentArchiveDetailsDialog';
import theme from '../../Theme';
import { DownloadSplitButton } from '../../components/buttons/DownloadSplitButton';
import DocumentArchiveListNoteDialog from '../../components/dialogs/DocumentArchiveListNoteDialog';


const ToolbarButton = styled(Button)`
     margin-right: 4px;
`;

interface DocumentArchivesProps {
    viewer: Viewer | undefined;
}

const DocumentArchives: React.FC<DocumentArchivesProps> = (props) => {
    const { premadeQuery } = useParams();
    const { viewer } = props;
    const dispatch = useAppDispatch();  
    const navigate = useNavigate();  
    const [isLoading, setIsLoading] = useState(false);
    const [contentAreaHeight, setContentAreaHeight] = useState('200px'); //need an actual default height so grid doesn't error on load
    const [openResendConfirm, setOpenResendConfirm] = useState(false);
    const [resendEmailsErrorMessage, setResendEmailsErrorMessage] = useState('');
    const [isResendingEmails, setIsResendingEmails] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [filtersCleared, setFiltersCleared] = useState(false);
    const [filterClientName, setFilterClientName] = useState<string | undefined>(undefined);
    const [filterClientId, setFilterClientId] = useState<string | undefined>(undefined);
    const [filterPartnerName, setFilterPartnerName] = useState<string | undefined>(undefined);
    const [filterPartnerId, setFilterPartnerId] = useState<string | undefined>(undefined);
    const [filterFileName, setFilterFileName] = useState<string | undefined>(undefined);
    const [filterControlNumber, setFilterControlNumber] = useState<string | undefined>(undefined);
    const [filterFunctionalGroupControlNumber, setFilterFunctionalGroupControlNumber] = useState<string | undefined>(undefined);
    const [filterTransactionControlNumber, setFilterTransactionControlNumber] = useState<string | undefined>(undefined);
    const [filterPurchaseOrder, setFilterPurchaseOrder] = useState<string | undefined>(undefined);
    const [filterErpOrderNumber, setFilterErpOrderNumber] = useState<string | undefined>(undefined);
    const [filterDocumentNumber, setFilterDocumentNumber] = useState<string | undefined>(undefined);
    const [filterStatuses, setFilterStatuses] = useState<DocumentAcknowledgementStatus[] | undefined>(undefined);
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(undefined);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(undefined);
    const [filterDirection, setFilterDirection] = useState<string | undefined>(undefined);
    const [filterDocumentType, setFilterDocumentType] = useState<string | undefined>(undefined);
    const [filterWasNotificationSent, setFilterWasNotificationSent] = useState<boolean | undefined>(undefined);
    const [filterIsNotificationRequired, setFilterIsNotificationRequired] = useState<boolean | undefined>(undefined);
    const [filterCount, setFilterCount] = useState(0);
    const [premadeFilter, setPremadeFilter] = useState<PremadeDocumentArchiveFilterSet | undefined | null>(null);
    const [documentArchiveRows, setDocumentArchiveRows] = useState<GridRowModel[] | undefined>(undefined);
    const [debounceOn, setDebounceOn] = useState(false);
    const [selectionModel, setSelectionModel] = useState<GridSelectionModel>();
    const [sortFirstLoad, setSortFirstLoad] = useState(true);
    const [waitForSort, setWaitForSort] = useState(false);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'createdDate',
            sort: 'desc',
        },
    ]);
    const [serverSortModel, setServerSortModel] = useState<DocumentArchiveOrderByInput[] | undefined>([{
        createdTime: OrderDirection.Desc
    } as DocumentArchiveOrderByInput]);
    const [pageSize, setPageSize] = useState(defaultGridListPageSize);
    const [pageSizeChanged, setPageSizeChanged] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsRowId, setDetailsRowId] = useState<string | undefined>(undefined);
    const [downloadXmlClicked, setDownloadXmlClicked] = useState(false);
    const [downloadRawEdiClicked, setDownloadRawEdiClicked] = useState(false);
    const [openNoteDetails, setOpenNoteDetails] = useState(false);
    const [noteType, setNoteType] = useState<string>('');
    
    const documentArchives = useAppSelector(selectDocumentArchiveList);
    const selectedDocumentArchive = useAppSelector(selectDocumentArchiveById);
    
    const error = useAppSelector(selectError);
    const requestsInFlight = useAppSelector(selectRequestsInFlight);
    const documentArchivesPagingResult = useAppSelector(selectDocumentArchivePagingResult);
    const getHtmlFileStatus = useAppSelector(selectGetDocumentHtmlFileStatus);
    const getAcknowledgementFileStatus = useAppSelector(selectGetDocumentAcknowledgementFileStatus);
    const abandonDocumentArchivesStatus = useAppSelector(selectAbandonDocumentArchivesStatus);
    const acknowledgeDocumentArchivesStatus = useAppSelector(selectAcknowledgeDocumentArchivesStatus);
    const resendEmailsStatus = useAppSelector(selectResendEmailsStatus);
    
    // New pagination state
    const currentPage = useAppSelector(selectCurrentPage);
    
    const canEdit = viewerCanEdit(viewer);

    

    useTitle("Document Archives");

    const debounceOnSortingChanged = useRef(
        debounce((servSortModel) => {
            onServerSortModelChanged(servSortModel);
        }, 1000)
    ).current;

    useEffect(() => {
        dispatch(clearState());
        setTimeout(() => {
            setDebounceOn(true);
        }, 200);
        if (premadeQuery) {
            applyPreset();
        } else {
            // Explicitly signal "no premade query" to allow filter bar to load from localStorage
            setPremadeFilter(undefined);
        }
    }, []);

    useEffect(() => {
        // when the pageSize changes and a filter exists, clear data and start new search with current filters
        if (pageSizeChanged) {
            setPageSizeChanged(false);
            if (filterCount > 0) {
                // Clear existing data that includes pagination state
                dispatch(clearState());
                // Start fresh search with new page size and current filters
                loadPage(undefined);
            }
        }
    }, [pageSizeChanged, filterCount]);

    useEffect(() => {
        if (documentArchivesPagingResult?.totalCount) {
            setTotalCount(documentArchivesPagingResult.totalCount);
        } else if (documentArchivesPagingResult === undefined) {
            // Reset total count to 0 when paging result is cleared (e.g., on page size change)
            setTotalCount(0);
        }
    }, [documentArchivesPagingResult?.totalCount, documentArchivesPagingResult]);

    const resetDownloadStatus = () => {
        // set processing flag back to false
        setIsDownloading(false);
        // remove get status
        dispatch(captureGetDocumentHtmlFileStatus());
        dispatch(captureGetDocumentAcknowledgementFileStatus());
        
        // set details id back to undefined if not in the details dialog
        if (!detailsOpen) {
            setDetailsRowId(undefined);
            // clear the selected document archive
            dispatch(fetchDocumentArchiveByIdSuccess());
        }
    }

    useEffect(() => {
        if (selectedDocumentArchive) {
            if (downloadXmlClicked) {
                if (selectedDocumentArchive.ediXml) {
                    let fileName = getUniqueFileName("ediXML", ".xml", selectedDocumentArchive.id);
                    // expecting ediXml to be in base64 from backend
                    downloadDocument(selectedDocumentArchive.ediXml, fileName, "text/xml", true, !detailsOpen);
                }
                setDownloadXmlClicked(false);
                // set processing flag back to false
                resetDownloadStatus();
            } else if (downloadRawEdiClicked) {
                if (selectedDocumentArchive.fileName && selectedDocumentArchive.ediDocument) {
                    // expecting ediDocument to be in base64 from backend
                    downloadDocument(selectedDocumentArchive.ediDocument, selectedDocumentArchive.fileName, "text/plain", true, !detailsOpen);
                }
                setDownloadRawEdiClicked(false);
                // set processing flag back to false
                resetDownloadStatus();
            }
        }
    }, [selectedDocumentArchive, downloadXmlClicked, downloadRawEdiClicked, detailsOpen]);

    useEffect(() => {
        if (getHtmlFileStatus?.result === RequestResult.Success) {
            let fileName = getUniqueFileName("htmlFile", ".html", detailsRowId);
            // get the base64 file from the data and call download
            var file = getHtmlFileStatus.data as string;
            downloadDocument(file, fileName, "text/html", true, !detailsOpen);
            resetDownloadStatus();
        } else if (getHtmlFileStatus?.result === RequestResult.Fail) {
            dispatch(setToastConfig({
                message: getHtmlFileStatus.message as string,
                severity: ToastSeverity.Error
            }));
            resetDownloadStatus();
        }
    }, [getHtmlFileStatus?.result]);

    useEffect(() => {
        if (getAcknowledgementFileStatus?.result === RequestResult.Success) {
            let fileName = getUniqueFileName("997File", ".txt", detailsRowId);
            // get the base64 file from the data and call download
            var file = getAcknowledgementFileStatus.data as string;
            downloadDocument(file, fileName, "text/plain", true, !detailsOpen);

            resetDownloadStatus();
        } else if (getAcknowledgementFileStatus?.result === RequestResult.Fail) {
            dispatch(setToastConfig({
                message: getAcknowledgementFileStatus.message as string,
                severity: ToastSeverity.Error
            }));
            resetDownloadStatus();            
        }
    }, [getAcknowledgementFileStatus?.result]);

    useEffect(() => {
        if (resendEmailsStatus?.result === RequestResult.Success) {
            dispatch(setToastConfig({
                message: resendEmailsStatus.message as string,
                severity: ToastSeverity.Success
            }));
            // remove resend status
            dispatch(captureResendEmailsStatus());
            // close the confirmation dialog and refresh
            onResendEmailsDialogClose();
            refreshList();
        } else if (resendEmailsStatus?.result === RequestResult.Fail) {
            setResendEmailsErrorMessage(resendEmailsStatus.message as string);
            setIsResendingEmails(false);
        }
    }, [resendEmailsStatus?.result]);

    useEffect(() => {
        if (acknowledgeDocumentArchivesStatus?.result === RequestResult.Success) {
            dispatch(setToastConfig({
                message: acknowledgeDocumentArchivesStatus.message as string,
                severity: ToastSeverity.Success
            }));
            // close the note dialog and refresh
            onAcknowledgeCompleted();
            onNoteDialogClose();
            refreshList();
        } else if (acknowledgeDocumentArchivesStatus?.result === RequestResult.Fail) {
            onAcknowledgeCompleted();
            onNoteDialogClose();
            dispatch(setToastConfig({
                message: acknowledgeDocumentArchivesStatus.message as string,
                severity: ToastSeverity.Error
            }));
        }
    }, [acknowledgeDocumentArchivesStatus?.result]);
    
    useEffect(() => {
        if (abandonDocumentArchivesStatus?.result === RequestResult.Success) {
            // close the abandon note dialog and refresh
            onAbandonCompleted(true);
            onNoteDialogClose();
            refreshList();
            dispatch(setToastConfig({
                message: abandonDocumentArchivesStatus.message as string,
                severity: ToastSeverity.Success
            }));
        } else if (abandonDocumentArchivesStatus?.result === RequestResult.Fail) {
            onAbandonCompleted(false);
            onNoteDialogClose();
            dispatch(setToastConfig({
                message: abandonDocumentArchivesStatus.message as string,
                severity: ToastSeverity.Error
            }));
        }
    }, [abandonDocumentArchivesStatus?.result]);
    
    useEffect(() => {
        setDocumentArchiveRows(getDocumentArchiveRows());

        if (!documentArchives && requestsInFlight > 0) {
            setIsLoading(true);
        } else if (requestsInFlight > 0) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [documentArchives, requestsInFlight]);

    useEffect(() => {
        // we have content, so lets properly size that content area        
        const titleHeight = document.getElementById('document-archives-title-comp')?.clientHeight || 0;
        // if filters are open, include that in the calculation
        let filterBarHeight = 0;
        if (filtersOpen) {
            filterBarHeight = document.getElementById('document-archives-filter-bar')?.clientHeight || 0;
        }
        let totalHeaderAreaHeight = titleHeight + filterBarHeight;
        if (totalHeaderAreaHeight > 0) {
            setContentAreaHeight(`calc(100% - ${totalHeaderAreaHeight}px)`);
        }
    }, [documentArchives, filtersOpen]);

    useEffect(() => {
        if (documentArchives) {
            let numFilters = 0;

            if (filterClientName && filterClientName.length > 0) {
                numFilters += 1;
            }
            if (filterDirection && filterDirection.length > 0) {
                numFilters += 1;
            }
            if (filterDocumentType && filterDocumentType.length > 0) {
                numFilters += 1;
            }
            if (filterPartnerName && filterPartnerName.length > 0) {
                numFilters += 1;
            }
            if (filterFileName && filterFileName.length > 0) {
                numFilters += 1;
            }
            if (filterControlNumber && filterControlNumber.length > 0) {
                numFilters += 1;
            }
            if (filterFunctionalGroupControlNumber && filterFunctionalGroupControlNumber.length > 0) {
                numFilters += 1;
            }
            if (filterTransactionControlNumber && filterTransactionControlNumber.length > 0) {
                numFilters += 1;
            }
            if (filterPurchaseOrder && filterPurchaseOrder.length > 0) {
                numFilters += 1;
            }
            if (filterErpOrderNumber && filterErpOrderNumber.length > 0) {
                numFilters += 1;
            }
            if (filterDocumentNumber && filterDocumentNumber.length > 0) {
                numFilters += 1;
            }
            if (filterStatuses && filterStatuses.length > 0) {
                numFilters += 1;
            }
            if (filterDateFrom) {
                numFilters += 1;
            }
            if (filterDateTo) {
                numFilters += 1;
            }
            if (filterWasNotificationSent !== undefined) {
                numFilters += 1;
            }
            if (filterIsNotificationRequired === true) {
                numFilters += 1;
            }

            setFilterCount(numFilters);
        }
    }, [documentArchives, filterClientName, filterDirection, filterDocumentType, filterPartnerName, filterFileName, filterControlNumber, filterFunctionalGroupControlNumber, filterTransactionControlNumber, filterPurchaseOrder, filterErpOrderNumber, filterDocumentNumber, filterStatuses, filterDateFrom, filterDateTo, filterWasNotificationSent, filterIsNotificationRequired]);

    useEffect(() => {
        return () => {
            debounceOnSortingChanged.cancel();
        };
    }, [debounceOnSortingChanged]);

    const getSortRefreshInput = () => {
        return {
            after: undefined,
            limit: pageSize,
            clientName: filterClientName,
            clientId: filterClientId,
            direction: filterDirection,
            documentType: filterDocumentType,
            order: serverSortModel,
            partnerName: filterPartnerName,
            partnerId: filterPartnerId,
            fileName: filterFileName,
            controlNumber: filterControlNumber,
            functionalGroupControlNumber: filterFunctionalGroupControlNumber,
            transactionControlNumber: filterTransactionControlNumber,
            purchaseOrder: filterPurchaseOrder,
            erpOrderNumber: filterErpOrderNumber,
            documentNumber: filterDocumentNumber,
            acknowledgementStatuses: filterStatuses,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            wasNotificationSent: filterWasNotificationSent,
            isNotificationRequired: filterIsNotificationRequired
        } as GetDocumentArchivesInput
    };

    useEffect(() => {
        if (!sortFirstLoad) {
            const sortRefreshData = getSortRefreshInput();
            if (debounceOn !== true) {
                onServerSortModelChanged(sortRefreshData);
            } else {
                debounceOnSortingChanged(sortRefreshData);
            }
        } else {
            setSortFirstLoad(false);
        }
    }, [serverSortModel]);
    

    const getDocumentArchiveRows = () => {
        if (documentArchives && documentArchives.length > 0) {
            return documentArchives.map((documentArchive) => {
                const {
                    id,
                    client,
                    controlNumber,
                    functionalGroupControlNumber,
                    transactionControlNumber,
                    purchaseOrder,
                    erpOrderNumber,
                    documentNumber,
                    documentType,
                    direction,
                    createdTime,
                    fileName,
                    acknowledgementStatus,
                    partner,
                    notifiedTime,
                    bulkNotify,
                    isAbandoned, 
                    internalTransactionId
                } = documentArchive;

                return {
                    _raw: documentArchive,
                    id,
                    clientName: client?.name,
                    clientId: client?.id,
                    controlNumber,
                    functionalGroupControlNumber,
                    transactionControlNumber,
                    PONumber: purchaseOrder,
                    erpOrderNumber,
                    documentNumber,
                    documentType,
                    direction,
                    createdDate: createdTime,
                    partnerName: partner?.name,
                    partnerId: partner?.id,
                    fileName,
                    status: acknowledgementStatus,
                    notifiedTime,
                    bulkNotify,
                    isAbandoned, 
                    internalTransactionId
                } as GridRowModel;
            }) as GridRowModel[];
        } else {
            return [];
        }
    };

    const onRowClientNameClick = (id: string | undefined) => {
        if (id) {
            let route = "/client/" + id;
            navigate(route);
        }
    };

    const onRowPartnerNameClick = (id: string | undefined) => {
        if (id) {
            let route = "/partner/" + id;
            navigate(route);
        }
    };

    const onDialogDownloadClick = (operation: DownloadOperation, archiveId: string, internalTransactionId?: string) => {
        if (operation === DownloadOperation.html) {
            onRowDownloadHtmlClick(archiveId, internalTransactionId);
          }
          else if (operation === DownloadOperation.raw) {
            if (!selectedDocumentArchive || selectedDocumentArchive.id !== archiveId) {
                onRowDownloadRawEdiClick(archiveId);
            } else {
                setDownloadRawEdiClicked(true);
            }
          }
          else if (operation === DownloadOperation.xml) {
            if (!selectedDocumentArchive || selectedDocumentArchive.id !== archiveId) {
                onRowDownloadXmlClick(archiveId);
            } else {
                setDownloadXmlClicked(true);
            }
          }
          else if (operation === DownloadOperation.ack) {
            onRowDownload997Click(archiveId, internalTransactionId);
          }
    };

    const onRowDownloadHtmlClick = (rowId: string, internalTransactionId?: string) => {
        if (internalTransactionId) {
            setIsDownloading(true);
            setDetailsRowId(rowId);
            dispatch(getDocumentHtmlFile((internalTransactionId)));
        }
    };

    const onRowDownload997Click = (rowId: string, internalTransactionId?: string) => {
        if (internalTransactionId) {
            setIsDownloading(true);
            setDetailsRowId(rowId);
            dispatch(getDocumentAcknowledgementFile(internalTransactionId));
        }
    };

    const onRowDownloadRawEdiClick = (rowId: string) => {
        setDownloadRawEdiClicked(true);
        setIsDownloading(true);
        dispatch(fetchDocumentArchiveById(rowId));
    };

    const onRowDownloadXmlClick = (rowId: string) => {
        setDownloadXmlClicked(true);
        setIsDownloading(true);
        dispatch(fetchDocumentArchiveById(rowId));
    };

    const documentArchiveColumns = useMemo<GridColumns<GridRowModel>>(
        () => [
            {
                headerName: 'CLIENT',
                field: 'clientName',
                flex: 2,
                cellClassName: "ediDataGridCellFirstChild",
                minWidth: 150,
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const clientId = row.clientId;
                    
                    if (clientId) {
                        return (
                            <Link
                                component="button"
                                className="ediDataGridCellFirstChild ediDataGridCellOverflowHidden"
                                color={row.isAbandoned ? theme.palette.warning.light : theme.palette.primary.main}
                                onClick={() => {
                                    onRowClientNameClick(clientId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'CONTROL #',
                field: 'controlNumber', 
                flex: 1,
                minWidth: 100,
                sortable: false,
            }, {
                headerName: 'FG #',
                field: 'functionalGroupControlNumber', 
                flex: 1,
                minWidth: 100,
                sortable: true,
            }, {
                headerName: 'TRANSACTION #',
                field: 'transactionControlNumber', 
                flex: 1,
                minWidth: 120,
                sortable: false,
            }, {
                headerName: 'PO #',
                field: 'PONumber',
                flex: 2,
                minWidth: 160,
                sortable: true,
            }, {
                headerName: 'ERP #',
                field: 'erpOrderNumber',
                flex: 1,
                minWidth: 100,
                sortable: false,
                hide: true,
            }, {
                headerName: 'I/O',
                field: 'direction',
                width: 80,
                sortable: true,
            }, {
                headerName: 'TYPE',
                field: 'documentType',
                width: 100,
                sortable: true,
            }, {
                headerName: 'DOC #',
                field: 'documentNumber',
                flex: 1,
                minWidth: 100,
                sortable: false,
            }, {
                headerName: 'CREATED DATE',
                field: 'createdDate',
                minWidth: 150,
                type: 'dateTime',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let dateValue = value ? getShortDateString(value) : undefined;
                    let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                    return (
                        <GridCellDualVert header={dateValue} sub={timeValue} />
                    )
                },
                sortable: true,
            }, {
                headerName: 'PARTNER',
                field: 'partnerName',
                minWidth: 140,
                flex: 1,
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    const partnerId = row.partnerId;
                    if (partnerId) {
                        return (
                            <Link
                                component="button"
                                variant="body2"
                                color={row.isAbandoned ? theme.palette.warning.light : theme.palette.primary.main}
                                className="ediDataGridCellOverflowHidden"
                                onClick={() => {
                                    onRowPartnerNameClick(partnerId);
                                }}
                            >
                                {value}
                            </Link>
                        );
                    } else {
                        return value;
                    }
                },
            }, {
                headerName: 'FILE NAME',
                field: 'fileName',
                flex: 1,
                minWidth: 130,
                sortable: false,
                resizable: true,
            }, {
                headerName: 'ACK STATUS',
                field: 'status',
                flex: 1,
                minWidth: 120,
                sortable: false,
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    const displayValue = capitalizeWithUnderscoreRemoval(value, " ");
                    return (displayValue);
                },
            }, {
                headerName: 'NOTIFIED TIME',
                field: 'notifiedTime',
                width: 114,
                sortable: true,
                type: 'dateTime',
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let dateValue = value ? getShortDateString(value) : undefined;
                    let timeValue = value ? getTimeString(value, { includeSeconds: true }) : undefined;
                    return (
                        <GridCellDualVert header={dateValue} sub={timeValue} />
                    )
                },
            }, {
                headerName: 'BULK NOTIFY',
                field: 'bulkNotify',
                width: 110,
                hide: true,
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let isNotify = value === true ? "Y" : "";
                    return (
                        isNotify
                    );
                },
                sortable: true,
            }, {
                headerName: 'ABANDONED',
                field: 'isAbandoned',
                cellClassName: 'ediDataGridCellWarning',
                width: 100,
                renderCell: (params: GridRenderCellParams) => {
                    const { value } = params;
                    let abandoned = value === true ? "Y" : "";
                    return (
                        abandoned
                    );
                },
                sortable: true,
            }, {
                field: 'id',
                sortable: false,
                headerName: '',
                width: 100,
                headerAlign: 'right',
                align: 'center',
                resizable: false,
                disableExport: true,
                // eslint-disable-next-line react/display-name
                renderCell: (params: GridRenderCellParams) => {
                    const { value, row } = params;
                    let ackStatus = row.status ? row.status as DocumentAcknowledgementStatus : undefined;
                    return (
                        <DownloadSplitButton
                            openInTab={true}
                            onDownloadHtmlClick={() => onRowDownloadHtmlClick(value, row.internalTransactionId)}
                            onDownloadRawEdiClick={() => onRowDownloadRawEdiClick(value)}
                            onDownloadXmlClick={() => onRowDownloadXmlClick(value)}
                            onDownloadAcknowledgementClick={ackStatus && ackStatus !== DocumentAcknowledgementStatus.Unacknowledged && ackStatus !== DocumentAcknowledgementStatus.NotApplicable ? () => onRowDownload997Click(value, row.internalTransactionId) : undefined}
                        />
                    );
                },
            }

        ],
        [],
    );

    const GetApiRef = () => {
        return useGridApiContext();
    };
    const loadingOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Document Archives' error={error}></ErrorMessage>
                )}
                {!error && !isDownloading && !detailsOpen && (documentArchives && documentArchives.length > 0 && requestsInFlight > 0) && (
                    <CircularProgress aria-label={'progress spinner'} key={'logs-spinner'} size={42} sx={{ zIndex: 1 }} />
                )}
                {!error && (!documentArchives) && (
                    <MainDataGridLoadingSkeleton apiRef={GetApiRef()} rowBottomMargin={6} />
                )}
            </GridOverlay>
        );
    };

    const noRowsOverlay = () => {
        return (
            <GridOverlay>
                {error && (
                    <ErrorMessage title='Unable to load the Document Archives' error={error}></ErrorMessage>
                )}
                {!error && (documentArchives && documentArchives.length <= 0 && filterCount > 0) && (
                    <NoResultsMessage topMargin={6} message="Try different filters to see results." />
                )}
            </GridOverlay>
        );
    };

    const handleExport = (options: GridCsvExportOptions, apiRef: React.MutableRefObject<GridApi>) => {
        // use the apiRef to directly call the export function
        // doing this instead of using the export button in the grid toolbar
        // to prevent having to choose the download option from the button dropdown
        apiRef?.current.exportDataAsCsv(options);
    };

    const gridToolbar = () => {
        let apiRef=GetApiRef();
        return (
            <GridToolbarContainer>
                {canEdit &&
                <ToolbarButton
                    title="Manually Acknowledge Selected"
                    aria-label="Acknowledge"
                    size="small"
                    onClick={acknowledgeSelectedRows}
                    color='primary'
                    disabled={!(selectionModel && selectionModel.length > 0)}
                >
                    Acknowledge
                </ToolbarButton>
                }
                {canEdit &&
                <ToolbarButton
                    title="Abandon Selected"
                    aria-label="Abandon"
                    size="small"
                    onClick={abandonSelectedRows}
                    color='error'
                    disabled={!(selectionModel && selectionModel.length > 0)}
                >
                    Abandon
                </ToolbarButton>
                }
                <ToolbarButton
                    title="Resend Email for Selected"
                    aria-label="Resend Email"
                    size="small"
                    onClick={resendEmailForSelectedRows}
                    color='primary'
                    disabled={!(selectionModel && selectionModel.length > 0)}
                >
                    Resend Email
                </ToolbarButton>
                    <ToolbarButton
                    title="Export to CSV"
                    aria-label="Export"
                    size="small"
                    onClick={() => handleExport({ allColumns: true }, apiRef)}
                    color='primary'
                    disabled={isLoading || !documentArchiveRows || documentArchiveRows.length === 0}
                >
                    Export
                </ToolbarButton>
            </GridToolbarContainer>
        );
    };

    const abandonSelectedRows = () => {
        // Open note dialog for abandon action
        openNoteDialogForAction('Abandon');
    };

    const acknowledgeSelectedRows = () => {
        // Open note dialog for acknowledge action
        openNoteDialogForAction('Acknowledge');
    };

    const resendEmailForSelectedRows = () => {
        setOpenResendConfirm(true);
    };

    const refreshList = () => {
        // Refresh list to bring in potential updates
        loadPage(undefined);
        // if details is open, refresh the details too
        if (detailsOpen && detailsRowId) {
            dispatch(fetchDocumentArchiveById(detailsRowId));
        }
    };

    const onDialogClose = () => {
        // Clear error on close.
        dispatch(clearError());
    };
    
    const onAbandonCompleted = (abandoned? : boolean) => {
        onSelectionModelChange([]); // de-select any selected rows
        onDialogClose();
        dispatch(captureAbandonDocumentArchivesStatus());
        if (detailsOpen && abandoned) {
            // if we abandoned from details, close the dialog
            onDetailsDialogClose();
        }
    };

    const onAcknowledgeCompleted = () => {
        onDialogClose();
        dispatch(captureAcknowledgeDocumentArchivesStatus());
    };

    const onResendEmailsDialogClose = () => {
        setOpenResendConfirm(false);
        onDialogClose();
        dispatch(captureResendEmailsStatus());
        setIsResendingEmails(false);
        setResendEmailsErrorMessage('');
    };

    const onResendEmailsConfirm = () => {
        // resend email for the selected archives
        let ids = getSelectionRows();
        if (ids) {
            setIsResendingEmails(true);
            dispatch(resendEmails(ids));
        }
    };

    const getServerSortEntryFromGridSortEntry = (entry: GridSortItem) => {
        let newModel = {} as DocumentArchiveOrderByInput;
        switch (entry.field) {
            case 'createdDate':
                newModel.createdTime = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'direction':
                newModel.direction = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'documentType':
                newModel.documentType = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'bulkNotify':
                newModel.bulkNotify = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'isAbandoned':
                newModel.isAbandoned = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'notifiedTime':
                newModel.notifiedTime = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'PONumber':
                newModel.purchaseOrderNumber = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'functionalGroupControlNumber':
                newModel.functionalGroupControlNumber = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'clientName':
                newModel.clientName = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
            case 'partnerName':
                newModel.partnerName = entry.sort === 'asc' ? OrderDirection.Asc : OrderDirection.Desc;
                break;
        }
        return newModel;
    };

    const createdAndSetServerSortModel = (model: GridSortModel) => {
        if (model && model.length > 0) {
            let newArray = [] as DocumentArchiveOrderByInput[];
            let i = 0;
            for (i; i < model.length; ++i) {
                newArray.push(getServerSortEntryFromGridSortEntry(model[i]));
            }
            setServerSortModel(newArray);
        } else {
            setServerSortModel(undefined);
        }

    };

    const onSortModelChange = (model: GridSortModel) => {
        createdAndSetServerSortModel(model);
        setSortModel(model);
    };

    const onFiltersClick = () => {
        // Show/hide filters bar
        setFiltersOpen(!filtersOpen);
        // reset filters cleared to false since not clicking close button on bar at this point
        setFiltersCleared(false);
    };

    const onFilterBarClose = () => {
        // set filters as cleared
        setFiltersCleared(true);
        setFilterCount(0);
        // we don't want to close the filter bar since client is required
        setFiltersOpen(true);
    };

    const onServerSortModelChanged = (refreshInput: GetDocumentArchivesInput) => {
        dispatch(clearState());
        dispatch(fetchDocumentArchiveList(1, refreshInput));
    };

    const onSelectionModelChange = (currentSelectionModel: GridSelectionModel) => {
        setSelectionModel(currentSelectionModel);
    };

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        setPageSizeChanged(true);
    };

    const loadPage = (targetPage?: number) => {
        const page = targetPage || 1;
        dispatch(fetchDocumentArchiveList(page, {
            limit: pageSize,
            clientName: filterClientName,
            clientId: filterClientId,
            direction: filterDirection,
            documentType: filterDocumentType,
            order: serverSortModel,
            partnerName: filterPartnerName,
            partnerId: filterPartnerId,
            fileName: filterFileName,
            controlNumber: filterControlNumber,
            functionalGroupControlNumber: filterFunctionalGroupControlNumber,
            transactionControlNumber: filterTransactionControlNumber,
            purchaseOrder: filterPurchaseOrder,
            erpOrderNumber: filterErpOrderNumber,
            documentNumber: filterDocumentNumber,
            acknowledgementStatuses: filterStatuses,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            wasNotificationSent: filterWasNotificationSent,
            isNotificationRequired: filterIsNotificationRequired
        }));
    };

    const handlePageChange = (newPage: number) => {
        // Use the unified pagination action for page navigation
        const filterInput = {
            limit: pageSize,
            clientName: filterClientName,
            clientId: filterClientId,
            direction: filterDirection,
            documentType: filterDocumentType,
            order: serverSortModel,
            partnerName: filterPartnerName,
            partnerId: filterPartnerId,
            fileName: filterFileName,
            controlNumber: filterControlNumber,
            functionalGroupControlNumber: filterFunctionalGroupControlNumber,
            transactionControlNumber: filterTransactionControlNumber,
            purchaseOrder: filterPurchaseOrder,
            erpOrderNumber: filterErpOrderNumber,
            documentNumber: filterDocumentNumber,
            acknowledgementStatuses: filterStatuses,
            dateFrom: filterDateFrom,
            dateTo: filterDateTo,
            wasNotificationSent: filterWasNotificationSent,
            isNotificationRequired: filterIsNotificationRequired
        } as GetDocumentArchivesInput;
        
        dispatch(fetchDocumentArchiveList(newPage, filterInput));
    };

    const refreshFilters = (filterOptions: FilterOptions) => {
        // Extract values from FilterOptions object
        const {
            filterClient,
            filterClientId: filterIdOfClient,
            filterIO,
            filterDocType,
            filterPartner,
            filterPartnerId: filterIdOfPartner,
            filterControlNum,
            filterFunctionalGroupControlNum,
            filterTransactionControlNum,
            filterPONum,
            filterFileNameVal,
            filterAcknowledgmentStatuses,
            filterCreatedDateFrom,
            filterCreatedDateTo,
            filterWasNotificationSent: filterNotificationSent,
            filterIsNotificationRequired: filterNotificationRequired,
            filterErpOrderNum,
            filterDocNum
        } = filterOptions;

        // convert status string array back into DocumentAcknowledgementStatus enum values for correct query type
        let ackStatuses: DocumentAcknowledgementStatus[] = [];
        if (filterAcknowledgmentStatuses && filterAcknowledgmentStatuses.length > 0) {
            filterAcknowledgmentStatuses.forEach((status: string) => {
                // put values back to all caps and add underscore back in where it was replaced with a space
                var allCapsStatus = status.replaceAll(' ', '_').toUpperCase();
                ackStatuses.push(allCapsStatus as DocumentAcknowledgementStatus);
            });
        }

        setFilterClientName(filterClient);
        setFilterClientId(filterIdOfClient);
        setFilterDirection(filterIO);
        setFilterDocumentType(filterDocType);
        setFilterControlNumber(filterControlNum);
        setFilterFunctionalGroupControlNumber(filterFunctionalGroupControlNum);
        setFilterTransactionControlNumber(filterTransactionControlNum);
        setFilterPurchaseOrder(filterPONum);
        setFilterErpOrderNumber(filterErpOrderNum);
        setFilterDocumentNumber(filterDocNum);
        setFilterPartnerName(filterPartner);
        setFilterPartnerId(filterIdOfPartner);
        setFilterFileName(filterFileNameVal);
        setFilterStatuses(ackStatuses);
        setFilterDateFrom(filterCreatedDateFrom);
        setFilterDateTo(filterCreatedDateTo);
        setFilterWasNotificationSent(filterNotificationSent);
        setFilterIsNotificationRequired(filterNotificationRequired);
        dispatch(clearState());
        
        // if in pre-made query for unset, the change of sort order will 
        // call fetch, so exit before calling fetch here to prevent
        // an initial search from firing prior to the sort being added
        if (premadeQuery && premadeQuery === "unsent" && waitForSort) {
            return;
        }

        dispatch(fetchDocumentArchiveList(1, {
            limit: pageSize,
            clientName: filterClient,
            clientId: filterIdOfClient,
            direction: filterIO,
            documentType: filterDocType,
            order: serverSortModel,
            partnerName: filterPartner,
            partnerId: filterIdOfPartner,
            fileName: filterFileNameVal,
            controlNumber: filterControlNum,
            functionalGroupControlNumber: filterFunctionalGroupControlNum,
            transactionControlNumber: filterTransactionControlNum,
            purchaseOrder: filterPONum,
            erpOrderNumber: filterErpOrderNum,
            documentNumber: filterDocNum,
            acknowledgementStatuses: ackStatuses,
            dateFrom: filterCreatedDateFrom,
            dateTo: filterCreatedDateTo,
            wasNotificationSent: filterNotificationSent,
            isNotificationRequired: filterNotificationRequired
        }));
    };

    const applyPreset = () => {
        if (premadeQuery) {
            if (premadeQuery === "rejected") {
                applyPresetRejectedFilter();
            } else if (premadeQuery === "unacknowledged") {
                applyPresetUnacknowledgedFilter();
            } else if (premadeQuery === "unsent") {
                applyPresetNotificationNotSentFilter();
            }
        }
    };

    const applyPresetUnacknowledgedFilter = () => {
        setPremadeFilter({
            filterAcknowledgmentStatuses: [DocumentAcknowledgementStatus.Unacknowledged],
            filterCreatedDateTo: documentArchiveUnacknowledgedFilterDate,
            filterIO: "O"
        } as PremadeDocumentArchiveFilterSet);
    };

    const applyPresetRejectedFilter = () => {
        setPremadeFilter({
            filterAcknowledgmentStatuses: [DocumentAcknowledgementStatus.Rejected, DocumentAcknowledgementStatus.PartialRejected],
            filterIO: "O"
        } as PremadeDocumentArchiveFilterSet);
    };

    const applyPresetNotificationNotSentFilter = () => {
        // set combination of notSent and notification required
        // since for the preset, we only want to see required ones
        setPremadeFilter({
            filterWasNotificationSent: false,
            filterIsNotificationRequired: true
        } as PremadeDocumentArchiveFilterSet);

        // default to sorting by bulkNotify desc to display those first
        // along with the usual createdDate
        // wrapped in a timeout to give time for the pre-made filter
        // values to be set in state first since the change of sort will 
        // trigger a search refresh/fetch and we want to wait for that search with the new sort
        setWaitForSort(true);
        setTimeout(() => {
            var sortModel: GridSortModel = [];
            sortModel.push({field: 'bulkNotify', sort: 'desc'});
            sortModel.push({field: 'createdDate', sort: 'desc'});
            onSortModelChange(sortModel);
            // after sort has been added to trigger a new search, reset flag for future searching
            setWaitForSort(false);
        }, 100);
    };

    const onRowClickHandler = (params: GridRowParams) => {
        if (params && params.id) {
            setDetailsRowId(params.id as string);
            setDetailsOpen(true);
        }
    };

    const onDetailsDialogClose = () => {
        setDetailsRowId(undefined);
        setDetailsOpen(false);
    };

    const onDetailsDialogResend = (rowId: string) => {
        if (rowId && rowId === detailsRowId) {
            setOpenResendConfirm(true);
        }
    };
    const onDetailsDialogAcknowledge = (rowId: string) => {
        if (rowId && rowId === detailsRowId) {
            //setOpenAcknowledgeConfirm(true);
            // Open note dialog for acknowledge action
            openNoteDialogForAction('Acknowledge');
        }
    };

    const onDetailsDialogAbandon = (rowId: string) => {
        if (rowId && rowId === detailsRowId) {
            //setOpenAbandon(true);
            // Open note dialog for abandon action
            openNoteDialogForAction('Abandon');
        }
    };

    const getResendConfirmationMessage = () => {
        if (detailsOpen) {
            // if the details dialog is open, then the resend came from there and it is a single request
            return `Are you sure you want to resend the email for this archive?`
        } else {
            // if not, the request is based off the selected rows            
           return `Are you sure you want to resend emails for the ${selectionModel?.length} selected archives?`;
        }
    };

    const getSelectionRows = () => {
        if (detailsOpen) {
            // if the details dialog is open, then the resend came from there and it is a single request
            if (detailsRowId) {
                return [detailsRowId];
            }
        } else {
            // if not, the request is based off the selected rows            
            if (selectionModel && selectionModel.length > 0) {
                return selectionModel.map(rowId => rowId.toString());
            }
        }
    };

    const openNoteDialogForAction = (action: 'Abandon' | 'Acknowledge') => {
        setNoteType(action);
        setOpenNoteDetails(true);
    };

    const onNoteDialogClose = () => {
        setOpenNoteDetails(false);
        setNoteType('');
    };

    const onNoteDialogSave = (note: string) => {
        if (note?.length) {
            // After capturing the note, perform the original action
            const ids = getSelectionRows();
            if (ids && noteType === 'Abandon') {
                dispatch(abandonDocumentArchives(ids, note));
            } else if (ids && noteType === 'Acknowledge') {
                dispatch(acknowledgeDocumentArchives(ids, note));
            }
        }
    };

    return (
        <MainContentBox>
            <PageTitleBar text='Document Archives' id="document-archives-title-comp">
                <PageTitleToolbarGrid item>
                    <FiltersButton
                        onClick={onFiltersClick}
                        filterCount={filterCount}
                        filtersCleared={filtersCleared}
                        disabled={isLoading}
                        aria-label="filter button"
                        data-cy="filters"
                    />
                </PageTitleToolbarGrid>
            </PageTitleBar>
            <DocumentArchivesFilterBar
                id="document-archives-filter-bar"
                visible={filtersOpen}
                loading={isLoading}
                viewer={viewer}
                premadeSet={premadeFilter}
                onFilterChanged={refreshFilters}
                onClose={onFilterBarClose}
            />
            <DataGridListScrollBox scrollheight={contentAreaHeight}>
                <MainDataGrid
                    loading={isLoading}
                    rowHeight={52}
                    aria-label="Document Archives List"
                    disableColumnMenu
                    disableColumnFilter
                    checkboxSelection
                    rows={documentArchiveRows ?? []}
                    columns={documentArchiveColumns}
                    sortingOrder={['asc', 'desc']}
                    sortModel={sortModel}
                    sortingMode="server"
                    onSortModelChange={onSortModelChange}
                    isRowSelectable={(params: GridRowParams) => params.row.isAbandoned !== true}
                    selectionModel={selectionModel}
                    onSelectionModelChange={onSelectionModelChange}
                    pagination={true}
                    paginationMode="server"
                    page={currentPage - 1}
                    onPageChange={(newPage: number) => handlePageChange(newPage + 1)}
                    pageSize={pageSize}
                    rowsPerPageOptions={pageSizeOptions}
                    onPageSizeChange={handlePageSizeChange}
                    rowCount={totalCount}
                    hideFooterSelectedRowCount
                    disableSelectionOnClick
                    onRowClick={onRowClickHandler}
                    getRowClassName={(params) => {
                        if (params.row.isAbandoned === true) {
                            return 'ediDataGridCellDisabled';
                        }
                            return '';
                    }}
                    components={{
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        LoadingOverlay: loadingOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        NoRowsOverlay: noRowsOverlay,
                        // eslint-disable-next-line react/display-name,@typescript-eslint/naming-convention
                        Toolbar: gridToolbar
                    }}
                    componentsProps={{
                        pagination: {
                            disabled: isLoading
                        }
                    }}
                    
                />
            </DataGridListScrollBox>
            <Dialog
                open={openResendConfirm}>
                <ConfirmationPrompt
                    heading="Resend Email"
                    message={getResendConfirmationMessage()}
                    handleConfirm={onResendEmailsConfirm}
                    handleReject={onResendEmailsDialogClose}
                    isBusy={isResendingEmails}
                    errorMessage={resendEmailsErrorMessage}
                />
            </Dialog>
            <DocumentArchiveDetailsDialog 
                isOpen={detailsOpen}
                viewer={viewer}
                documentArchiveRecordId={detailsRowId}
                userCanEdit={canEdit}
                onAcknowledge={onDetailsDialogAcknowledge}
                onClose={onDetailsDialogClose}
                onAbandon={onDetailsDialogAbandon}
                onResendEmail={onDetailsDialogResend}
                onDownloadClick={onDialogDownloadClick}
                onClientNameClick={onRowClientNameClick}
                onPartnerNameClick={onRowPartnerNameClick}
                error={error}
            />
            <DocumentArchiveListNoteDialog
                isOpen={openNoteDetails}
                type={noteType}
                onCancel={onNoteDialogClose}
                onSave={onNoteDialogSave}
            />
            {(isDownloading) &&
                <CircularProgress
                    aria-label={'progress spinner'}
                    size={48}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-24px',
                        marginLeft: '-24px',
                        zIndex: 1500
                    }}
                />
            }
        </MainContentBox>
    );
}

export default DocumentArchives;